//
// Copyright (c) 2011-2022 by Curalate, Inc.
//

import {
  EcommerceSessionType,
  SortDirection,
  SortKey,
} from '@apis/metrics/metricsApiModel';
import { SCREEN_CAPTURE_PAGES } from '@apis/screen-capture/screenCaptureApiModels';
import { IInsightsSidebarPage } from '@insights/insightsSidebarConstants';
import { IInsightsActivityStats } from '@insights/redux/insightsPageModel';
import { FormattedTopPerformersItemProductStat } from '@insights/redux/selectors/getFormattedInsightsStats/getFormattedInsightsActivityStats';
import {
  EcomConnectionState,
  IInsightsEcommerceProviderState,
} from '@insights/redux/selectors/getInsightsEcomConnection';
import Cuid from '@models/Cuid';
import ItemId from '@models/ItemId';
import { EcommerceProvider } from '@models/MetricsGatewayApiModels';
import PartialCuid from '@models/PartialCuid';
import { TrackingId, TrackingIdPrefix } from '@models/TrackingId';
import { IUnisonDateRange } from '@models/UnisonTypes';
import { UniversalItemType } from '@models/UniversalItemType';
import { itemToPartialCuid } from '@utils/partialCuidUtils';

import { RoutableStateName } from 'app/top-level-states';

export enum TopPerformersContentType {
  MEDIA = 'Media',
  CONTRIBUTORS = 'Contributors',
  PRODUCT = 'Products',
  REVEALS = 'Reveals',
  SHOWROOM = 'Showroom',
}

export enum TopPerformersTableView {
  TABLE = 'table',
  GRID = 'grid',
}

export const TOP_PERFOMERS_POSSIBLE_NUMBER_OF_RESULTS = [8, 16, 24, 32];

export const initialBaseStatsRequest: IInsightsActivityStats = {
  contentType: TopPerformersContentType.MEDIA,
  view: TopPerformersTableView.GRID,
  sortKey: SortKey.INTERACTIVE_VIEWS,
  sortDir: SortDirection.DESC,
  limit: 8,
  offset: 0,
  itemStats: undefined,
};

export interface TouchpointBreakdownHeader {
  title: string;
  tooltip?: string;
  shouldDisplay: (
    providerState: IInsightsEcommerceProviderState,
    provider?: EcommerceProvider,
    route?: RoutableStateName,
  ) => boolean;
  isEcom: boolean;
}

export const INSIGHTS_TOOLTIPS = {
  SUMMARY_TABS: {
    VISITS: 'Total sessions across all Vibe Commerce experiences',
    CLICKTHROUGHS:
      'Total times that users followed a link to your website across all Vibe Commerce experiences',
    TRANSACTIONS:
      'Total orders attributed to Galleries using the e-commerce attribution model set on the Settings page, plus orders attributed to the clickthroughs to site from all other Vibe Commerce experiences',
    REVENUE:
      'Transaction revenue attributed to on-site Vibe Commerce experiences as defined by the e-commerce attribution model set on the Settings page, plus' +
      ' revenue attributed to clickthroughs to site from Commerce experiences',
  },
  GALLERIES_TABS: {
    VISITS: 'Total on-site sessions that viewed a Gallery',
    CLICKTHROUGHS:
      'Total times that users followed a link to your website from your Gallery',
    TRANSACTIONS:
      'Orders attributed to Gallery as defined by the e-commerce attribution model set on the Settings page',
    REVENUE:
      'Transaction revenue attributed to Gallery as defined by the e-commerce attribution model set on the Settings page',
  },
  L2B_TABS: {
    VISITS: 'Total sessions that viewed your Instagram Like2Buy store',
    CLICKTHROUGHS:
      'Total times that users followed a link to your website from your Instagram Like2Buy store',
    TRANSACTIONS:
      'Orders made from clickthroughs to your website from your Instagram Like2buy store',
    REVENUE:
      'Transaction revenue attributed to clickthroughs to site from Instagram LikeeBuy',
  },
  L2B_V2_TABS: {
    VISITS: 'Total sessions that viewed your TikTok Like2Buy store',
    CLICKTHROUGHS:
      'Total times that users followed a link to your website from your TikTok Like2Buy store',
    TRANSACTIONS:
      'Orders made from clickthroughs to your website from your TikTok Like2buy store',
    REVENUE:
      'Transaction revenue attributed to clickthroughs to site from TikTok Like2Buy',
  },
  L2B_SUMMARY_TABS: {
    VISITS: 'Total sessions that viewed your Like2Buy stores',
    CLICKTHROUGHS:
      'Total times that users followed a link to your website from your Like2Buy stores',
    TRANSACTIONS:
      'Orders made from clickthroughs to your website from your Like2buy stores',
    REVENUE:
      'Transaction revenue attributed to clickthroughs to site from Like2Buy',
  },
  REVEAL_TABS: {
    VISITS: 'Total on-site sessions that viewed a Reveal',
    CLICKTHROUGHS:
      'Total times that users followed a link to your website from your Reveals',
    TRANSACTIONS: 'Orders attributed to the clickthroughs to site from Reveals',
    REVENUE:
      'Transaction revenue attributed to clickthroughs to site from Reveals',
  },
  SHOWROOM_TABS: {
    VISITS: 'Total sessions that viewed a Showroom',
    CLICKTHROUGHS:
      'Total times that users followed a link to your website from your Showrooms',
    TRANSACTIONS:
      'Orders attributed to the clickthroughs to site from Showrooms',
    REVENUE:
      'Transaction revenue attributed to clickthroughs to site from Showrooms',
  },
  CTR: 'Total visits that include a clickthrough divided by total visits',
  CONVERSION_RATE:
    'Total visits that resulted in an order divided by total visits',
  AOV: 'Average revenue for each transaction made through clickthroughs to your website',
  FUNNEL_CHART: 'Shows how visits and clickthroughs lead to revenue',
  INTERACTIVE_VIEWS:
    'Total number of page views with at least one click or hover across all Vibe Commerce experiences',
  ATTRIBUTION_MODEL:
    'Galleries ecommerce association settings are used to attribute ecommerce metrics to your Galleries based on user-actions you would like to see.',
};

export enum InsightsTabIndex {
  Visits = 0,
  Clicks = 1,
  Transactions = 2,
  Revenue = 3,
}

export const INSIGHTS_LABELS = {
  INTERACTIVE_VIEWS: {
    full: 'Interactive Views',
    abbreviated: 'Int. Views',
  },
  VISITS: {
    full: 'Visits',
  },
  CLICKTHROUGHS: {
    full: 'Clickthroughs',
  },
  CTR: {
    abbreviated: 'CTR',
    full: 'Clickthrough Rate',
    combined: 'CTR (Clickthough Rate)',
  },
  TRANSACTIONS: {
    full: 'Transactions',
  },
  CONVERSION_RATE: {
    abbreviated: 'Conv. Rate',
    full: 'Conversion Rate',
  },
  REVENUE: {
    full: 'Revenue',
  },
  AOV: {
    abbreviated: 'AOV',
    full: 'Average Order Value',
    combined: 'AOV (Average Order Value)',
  },
  ATTRIBUTION_MODEL: {
    full: 'Attribution Model',
  },
};

export const TouchpointBreakdownHeaders: TouchpointBreakdownHeader[] = [
  {
    title: INSIGHTS_LABELS.VISITS.full,
    tooltip: 'Total sessions that viewed this experience',
    shouldDisplay: (_providerState, _provider, _route) => {
      return true;
    },
    isEcom: false,
  },
  {
    title: INSIGHTS_LABELS.INTERACTIVE_VIEWS.abbreviated,
    tooltip:
      'Total number of page views with at least one click or hover across all Vibe Commerce experiences',
    shouldDisplay: (_providerState, _provider, _route) => {
      return true;
    },
    isEcom: false,
  },
  {
    title: INSIGHTS_LABELS.CLICKTHROUGHS.full,
    tooltip:
      'Total number of clickthroughs to your site across all Vibe Commerce experiences',
    shouldDisplay: (_providerState, _provider, _route) => {
      return true;
    },
    isEcom: false,
  },
  {
    title: INSIGHTS_LABELS.CTR.abbreviated,
    tooltip: `Clickthrough Rate is the total sessions that include a clickthrough to site divided by total sessions`,
    shouldDisplay: (_providerState, _provider, _route) => {
      return true;
    },
    isEcom: false,
  },
  {
    title: INSIGHTS_LABELS.TRANSACTIONS.full,
    tooltip: `Total orders attributed to Galleries using the e-commerce attribution model set on the Settings page, plus orders attributed to the clickthroughs to site from all other Vibe Commerce experiences`,
    shouldDisplay: (providerState, _provider, _route) => {
      return providerState.ecomConnectionState !== EcomConnectionState.None;
    },
    isEcom: true,
  },
  {
    title: INSIGHTS_LABELS.CONVERSION_RATE.abbreviated,
    tooltip: `Conversion Rate is the total on-site sessions that resulted in an order divided by total on-site sessions`,
    shouldDisplay: (providerState, _provider, _route) => {
      return providerState.ecomConnectionState !== EcomConnectionState.None;
    },
    isEcom: true,
  },
  {
    title: INSIGHTS_LABELS.AOV.abbreviated,
    tooltip: `Average Order Value is the average revenue generated for each transaction attributed to this experience`,
    shouldDisplay: (providerState, _provider, _route) => {
      return providerState.ecomConnectionState !== EcomConnectionState.None;
    },
    isEcom: true,
  },
  {
    title: INSIGHTS_LABELS.REVENUE.full,
    tooltip: `Transaction revenue attributed to on-site Vibe Commerce experiences as defined by the e-commerce attribution model set on the Settings page, plus revenue attributed to clickthroughs to site from all other Vibe Commerce experiences`,
    shouldDisplay: (providerState, _provider, _route) => {
      return providerState.ecomConnectionState !== EcomConnectionState.None;
    },
    isEcom: true,
  },
];

export const INSIGHTS_PRODUCT_CARD_HEIGHT = 406;
export const INSIGHTS_MEDIA_CARD_HEIGHT = 437;
export const INSIGHTS_PRODUCT_CARD_HEIGHT_NO_ECOM = 348;
export const INSIGHTS_MEDIA_CARD_HEIGHT_NO_ECOM = 379;
export const INSIGHTS_GRID_CONTENT_CARD_MEDIA_HEIGHT = 224;
export const INSIGHTS_CONTENT_GRID_WIDTH = 979;

// Insights Summary PDF Export specs
export const INSIGHTS_TOP_CONTENT_CARD_HEIGHT = 414;
export const INSIGHTS_TOP_CONTENT_CARD_HEIGHT_NO_ECOM = 362;

export enum ContentCoverageTableView {
  TABLE = 'table',
  GRID = 'grid',
}

export const CONTENT_COVERAGE_DEFAULT_LIMIT = 8;
export const CONTENT_COVERAGE_DEFAULT_PAGE_NUMBER = 0;
export const CONTENT_COVERAGE_LIMIT_OPTIONS = [8, 16, 32, 64];

export const BENCHMARK_EMPTY_CHART_VALUES: [number, number] = [140, 100];

export const getPartialCuid = (item: FormattedTopPerformersItemProductStat) => {
  if (item.stats.mediaInfo.cuid) {
    const parsedCuid = Cuid.parse(item.stats.mediaInfo.cuid);
    return PartialCuid.build({
      itemType: parsedCuid.itemType,
      id: parsedCuid.itemId,
    });
  }
  switch (item.stats.mediaInfo.type) {
    case 'ProductInfo':
      return itemToPartialCuid(
        UniversalItemType.Product,
        ItemId(item.stats.mediaInfo.itemInfo.fileId ?? ''),
      );
    case 'ItemInfo':
    default:
      return itemToPartialCuid(
        UniversalItemType.MediaImport,
        ItemId(item.stats.mediaInfo.itemInfo.fileId ?? ''),
      );
  }
};

export const getReadableDateRange = (dateRange: IUnisonDateRange) => {
  const formatDateRange = () => {
    const formatDate = (date: Date) => {
      return date.toLocaleDateString();
    };
    const startDateString = formatDate(dateRange.startDate);
    const endDateString = formatDate(dateRange.endDate);
    if (startDateString != endDateString) {
      return `from ${startDateString} - ${endDateString}`;
    } else {
      return `on ${startDateString}`;
    }
  };

  switch (dateRange.alias) {
    case 'last24Hours':
      return 'last 24 hours';
    case 'last7Days':
      return 'last 7 days';
    case 'last30Days':
      return 'last 30 days';
    case 'last3Months':
      return 'last 3 months';
    case 'last1Year':
      return 'last year';
    case 'custom':
    default:
      return formatDateRange();
  }
};

export const withHttp = (link: string) => {
  let linkWithHttp = link;
  if (!linkWithHttp.startsWith('http')) {
    linkWithHttp = `http://${linkWithHttp}`;
  }
  return linkWithHttp;
};

// This is currently used to show the Summary PDF download if we are hiding the summary page
export const getSingleFeaturePdf = (
  navItemsWithAccess?: IInsightsSidebarPage[],
) => {
  return (navItemsWithAccess ?? []).length == 1
    ? SCREEN_CAPTURE_PAGES.INSIGHTS_SUMMARY
    : undefined;
};

export const InsightsPrefix: TrackingIdPrefix = 'insights-';
export const RightsInsightsPrefix: TrackingIdPrefix = `${InsightsPrefix}rights-`;

export const InsightsTrackingIds = {
  ATTRIBUTION_MODEL_SELECT: `${InsightsPrefix}attribution-model-select`,
  BENCHMARKS_MODULE: `${InsightsPrefix}benchmarks-module`,
  BENCHMARKS_MODULE_ADVANCED_TIER_SUPPORT_LINK: `${InsightsPrefix}benchmarks-module-advanced-tier-support-link`,
  BENCHMARKS_MODULE_ADVANCED_TIER_ARTICLE_LINK: `${InsightsPrefix}benchmarks-module-advanced-tier-article-link`,
  CONTENT_COVERAGE_PDP_LEARN_MORE_LINK: `${InsightsPrefix}content-coverage-pdp-learn-more-link`,
  CONTENT_COVERAGE_PDP_POPULAR_PRODUCTS_LEARN_MORE_LINK: `${InsightsPrefix}content-coverage-pdp-popular-products-learn-more-link`,
  CONTENT_COVERAGE_PDP_POPULAR_PRODUCTS_LINK: `${InsightsPrefix}content-coverage-pdp-popular-products-link`,
  CONTENT_COVERAGE_PDP_POPULAR_PRODUCTS_SUPPORT_LINK: `${InsightsPrefix}content-coverage-pdp-popular-products-support-link`,
  CONTENT_COVERAGE_PDP_SUPPORT_LINK: `${InsightsPrefix}content-coverage-pdp-support-link`,
  CONTENT_COVERAGE_SUMMARY_SUPPORT_LINK: `${InsightsPrefix}content-coverage-summary-support-link`,
  CSV_DOWNLOAD: `${InsightsPrefix}csv-download`,
  DATE_PICKER: `${InsightsPrefix}date-picker`,
  LABEL_FILTERING_SELECT: `${InsightsPrefix}label-filtering-select`,
  LIFT_MODULE: `${InsightsPrefix}lift-module`,
  LIFT_MODULE_ADVANCED_TIER_SUPPORT_LINK: `${InsightsPrefix}lift-module-advanced-tier-support-link`,
  OPT_IN_LINK: `${InsightsPrefix}opt-in-link`,
  OPT_IN_OUT_HELP_LINK: `${InsightsPrefix}opt-in-out-help-link`,
  OPT_OUT_LINK: `${InsightsPrefix}opt-out-link`,
  PDF_DOWNLOAD: `${InsightsPrefix}pdf-download`,
  PRODUCTS_WITHOUT_COVERAGE_DOWNLOAD: `${InsightsPrefix}products-without-coverage-download`,
  PRODUCTS_WITHOUT_COVERAGE_SECTION: `${InsightsPrefix}products-without-coverage-section`,
  RIGHTS_INSIGHTS_DATE_PICKER: `${RightsInsightsPrefix}date-picker`,
  RIGHTS_INSIGHTS_MESSAGE_PERFORMANCE_PREFIX: `${RightsInsightsPrefix}message-performance-`,
  STAT_TRENDS_CHART: `${InsightsPrefix}stat-trends-chart`,
  STAT_TRENDS_FUNNEL_CHART: `${InsightsPrefix}stat-trends-funnel-chart`,
  STAT_TRENDS_SECTION: `${InsightsPrefix}stat-trends-section`,
  STAT_TRENDS_TAB_CLICKS: `${InsightsPrefix}stat-trends-tab-clicks`,
  STAT_TRENDS_TAB_REVENUE: `${InsightsPrefix}stat-trends-tab-revenue`,
  STAT_TRENDS_TAB_TRANSACTIONS: `${InsightsPrefix}stat-trends-tab-transactions`,
  STAT_TRENDS_TAB_VISITS: `${InsightsPrefix}stat-trends-tab-visits`,
  SUMMARY_CONTENT_COVERAGE_LINK: `${InsightsPrefix}summary-content-coverage-link`,
  SUMMARY_CONTENT_COVERAGE_SECTION: `${InsightsPrefix}summary-content-coverage-section`,
  SUMMARY_LEARN_MORE_LINK: `${InsightsPrefix}summary-learn-more-link`,
  // TODO: PD-256187 remove after we have determined users are comfortable with the revenue changes
  SUMMARY_UPDATED_REVENUE_BANNER_LINK: `${InsightsPrefix}summary-updated-revenue-banner-link`,
  GALLERIES_UPDATED_REVENUE_BANNER_LINK: `${InsightsPrefix}galleries-updated-revenue-banner-link`,
  GALLERIES_DETAILS_UPDATED_REVENUE_BANNER_LINK: `${InsightsPrefix}galleries-details-updated-revenue-banner-link`,
} as const satisfies Record<string, TrackingId>;

export const HELP_LINKS = {
  ANALYTICS_DATE_RESTRICTIONS:
    'https://support.bazaarvoice.com/s/article/Curalate-Insights-Data-Limitations-by-Date-Range-and-eCommerce-Provider',
  GALLERIES: '/galleries/website',
  LIKE2BUY: '/instagram/like2buy',
  OVERVIEW:
    'https://support.bazaarvoice.com/s/article/Curalate-Insights-Overview',
  REVEAL: 'https://support.bazaarvoice.com/s/article/Reveal-Overview',
  SHOWROOM: 'https://support.bazaarvoice.com/s/article/Showroom-Overview',
  SUPPORTED_ANALYTICS_PROVIDERS:
    'https://support.bazaarvoice.com/s/article/Supported-Analytics-Providers-at-Curalate',
} as const;

export const BAZAARVOICE_UPGRADE_LEARN_MORE_LINK =
  'https://support.bazaarvoice.com/s/article/Bazaarvoice-Vibe-Tiers?language=en_US';

export const DEFAULT_REVENUE_ATTRIBUTION_MODEL = EcommerceSessionType.CLICK;
